import { Box } from "@mui/material";
import { useGridApiRef } from "@mui/x-data-grid";
import { useState } from "react";
import useAPI from "../../../../../../api/hooks/useAPI";
import ExportButton from "../../../../../../components/buttons/ExportButton";
import CustomToolTip from "../../../../../../components/CustomTooltip";
import SearchInput from "../../../../../../components/filters/SearchInput";
import StyledDataGrid from "../../../../../../components/StyledDataGrid";
import { useFileNaming } from "../../../../../../hooks/useFileNaming";
import { ComplexEntityTypes } from "../../../../../../utils/constants";
import IntegrationImage from "../../../../integrations/components/IntegrationImage";
import { useEntities } from "../../../hooks/useEntities";
import EntityDrawer from "../../EntityDrawer/EntityDrawer";
import TextIntegrationStyle from "../../TextIntegrationStyle";
import { getUserRelatedAppsRows } from "./checkRowsUserData";

const UserAppView = ({ entityData }) => {
  const { useQueryForEntitiesByType } = useEntities();
  const { trackEvent } = useAPI();
  const { generateBreezeFileName } = useFileNaming();
  const gridRef = useGridApiRef();

  const { data: appEntities } = useQueryForEntitiesByType({
    type: ComplexEntityTypes.APP,
  });

  const [clickedAppEntity, setClickedAppEntity] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState({
    searchString: "",
  });

  const allUserApps = appEntities
    ? getUserRelatedAppsRows(entityData, appEntities)
    : [];

  let filteredAppRows = allUserApps;
  if (appliedFilters.searchString) {
    const lowerCaseSearchString = appliedFilters.searchString.toLowerCase();
    filteredAppRows = allUserApps.filter((row) =>
      row?.name?.toLowerCase().includes(lowerCaseSearchString)
    );
  }

  const onAppClick = (params) => {
    trackEvent(
      `User clicked on app ID ${params?.row?.appId}, from user connected apps view ${entityData?.display_name}`
    );
    setClickedAppEntity(params.row.entityObj);
  };

  const handleTableExport = () => {
    trackEvent(
      `User clicked export button for user apps - ${entityData?.display_name}`
    );
    const userName =
      entityData?.display_name?.replace(/[^a-zA-Z0-9]/g, "_") || "user";
    const csvExportOptions = {
      fileName: generateBreezeFileName(`user_${userName}_apps`),
    };
    gridRef.current?.exportDataAsCsv(csvExportOptions);
  };

  const columnsToShow = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      renderCell: (params) => (
        <CustomToolTip title="Click to view app details">
          <Box>
            <TextIntegrationStyle title={""} value={params?.value} />
          </Box>
        </CustomToolTip>
      ),
    },
    {
      field: "source",
      headerName: "Source",
      flex: 1,
      renderCell: (params) => (
        <CustomToolTip title="Click to view app details">
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            {params.row.source.map((source, index) => (
              <IntegrationImage
                key={index}
                name={source}
                width="16px"
                newLogo={true}
              />
            ))}
          </Box>
        </CustomToolTip>
      ),
    },
  ];

  return (
    <>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ width: "300px" }}>
          <SearchInput
            fieldName="searchString"
            appliedFilters={appliedFilters}
            setAppliedFilters={setAppliedFilters}
          />
        </Box>
        <ExportButton
          handleExport={handleTableExport}
          disabled={!filteredAppRows || filteredAppRows.length === 0}
        />
      </Box>
      <Box sx={{ mt: 2 }}>
        <StyledDataGrid
          apiRef={gridRef}
          columns={columnsToShow}
          rows={filteredAppRows}
          onRowClick={onAppClick}
          style={{ cursor: "pointer" }}
        />
      </Box>

      <EntityDrawer
        openDrawer={clickedAppEntity}
        selectedEntity={clickedAppEntity}
        onClose={() => setClickedAppEntity(null)}
      />
    </>
  );
};
export default UserAppView;
